// Column-config helpers shared by the Customize Columns pickers (TableColumnPreferenceControl,
// LocalStorageColumnPreferenceControl) and the popover they both render.

export const getPreferenceSettings = (column = {}) => column.preferenceSettings || {}

export const isColumnVisible = (column = {}) => {
    const preferenceSettings = getPreferenceSettings(column)
    if (typeof preferenceSettings.isVisible === 'boolean') return preferenceSettings.isVisible
    return false
}

export const canToggleColumnVisibility = (column = {}) => {
    const preferenceSettings = getPreferenceSettings(column)
    if (typeof preferenceSettings.canToggleVisibility === 'boolean') return preferenceSettings.canToggleVisibility
    if (typeof preferenceSettings.canHide === 'boolean') return preferenceSettings.canHide
    if (typeof column.showInPicker === 'boolean') return column.showInPicker
    return false
}

export const canReorderColumn = (column = {}) => {
    const preferenceSettings = getPreferenceSettings(column)
    if (typeof preferenceSettings.canReorder === 'boolean') return preferenceSettings.canReorder
    if (typeof preferenceSettings.positionIsFixed === 'boolean') return !preferenceSettings.positionIsFixed
    if (typeof column.positionIsFixed === 'boolean') return !column.positionIsFixed
    return false
}

export const hasPickerControls = (column = {}) => canToggleColumnVisibility(column) || canReorderColumn(column)

export const withPreferenceSettings = (column = {}, overrides = {}) => {
    const preferenceSettings = {
        ...getPreferenceSettings(column),
        ...overrides,
    }

    return {
        ...column,
        preferenceSettings,
    }
}

// What a config looks like to the picker: which columns show, in what order.
export const columnConfigSignature = (columns = []) => JSON.stringify(
    columns.map((column) => ({
        id: column.id,
        isVisible: isColumnVisible(column),
        order: column.order,
    }))
)

// With no saved preference every column shows, except an optional one declared with
// preferenceSettings.defaultVisible: false, which waits to be picked.
export const buildColumnConfigFromPreference = (defaultColumns = [], preferenceData) => {
    const fixedColumns = defaultColumns
        .filter((column) => !canReorderColumn(column))
        .map((column, index) => ({ ...withPreferenceSettings(column, { isVisible: true }), order: index }))

    const fixedIds = new Set(fixedColumns.map((column) => column.id))
    const knownMap = new Map(defaultColumns.map((column) => [column.id, column]))

    if (!Array.isArray(preferenceData) || preferenceData.length === 0) {
        const visibleColumns = defaultColumns
            .filter((column) => canReorderColumn(column))
            .map((column, index) => ({
                ...withPreferenceSettings(column, { isVisible: getPreferenceSettings(column).defaultVisible !== false }),
                order: fixedColumns.length + index,
            }))
        return [...fixedColumns, ...visibleColumns]
    }

    const visibleIds = new Set(preferenceData)
    const orderedVisibleColumns = preferenceData
        .filter((columnId) => !fixedIds.has(columnId))
        .map((columnId, index) => {
            if (knownMap.has(columnId)) {
                return { ...withPreferenceSettings(knownMap.get(columnId), { isVisible: true }), order: fixedColumns.length + index }
            }

            return {
                id: columnId,
                name: columnId,
                preferenceSettings: {
                    isVisible: true,
                    canToggleVisibility: true,
                    canReorder: true,
                },
                order: fixedColumns.length + index,
                isCustom: true,
            }
        })

    const hiddenColumns = defaultColumns
        .filter((column) => canReorderColumn(column) && !visibleIds.has(column.id))
        .map((column, index) => ({
            ...withPreferenceSettings(column, { isVisible: false }),
            order: fixedColumns.length + orderedVisibleColumns.length + index,
        }))

    return [...fixedColumns, ...orderedVisibleColumns, ...hiddenColumns]
}
